import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Beautiful custom Tooltip component with smart positioning
 * Uses portal to render tooltip at document root to avoid overflow issues
 *
 * @param {React.ReactNode} children - The element that triggers the tooltip
 * @param {React.ReactNode} content - The tooltip content (can be string or JSX)
 * @param {string} position - Preferred position: 'top' | 'bottom' | 'left' | 'right' (default: 'top')
 * @param {number} delay - Delay before showing tooltip in ms (default: 300)
 * @param {string} className - Additional classes for the wrapper
 */
export default function Tooltip({
  children,
  content,
  position = 'top',
  delay = 300,
  className = ''
}) {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const [arrowStyle, setArrowStyle] = useState({});
  const [actualPosition, setActualPosition] = useState(position);
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const timeoutRef = useRef(null);

  const calculatePosition = useCallback(() => {
    if (!triggerRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipEl = tooltipRef.current;
    const padding = 8; // Gap between trigger and tooltip
    const viewportPadding = 12; // Minimum distance from viewport edge

    // Get tooltip dimensions (estimate if not yet rendered)
    const tooltipWidth = tooltipEl?.offsetWidth || 150;
    const tooltipHeight = tooltipEl?.offsetHeight || 40;

    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight
    };

    let top = 0;
    let left = 0;
    let finalPosition = position;

    // Calculate initial position based on preferred position
    switch (position) {
      case 'top':
        top = triggerRect.top - tooltipHeight - padding;
        left = triggerRect.left + triggerRect.width / 2 - tooltipWidth / 2;
        break;
      case 'bottom':
        top = triggerRect.bottom + padding;
        left = triggerRect.left + triggerRect.width / 2 - tooltipWidth / 2;
        break;
      case 'left':
        top = triggerRect.top + triggerRect.height / 2 - tooltipHeight / 2;
        left = triggerRect.left - tooltipWidth - padding;
        break;
      case 'right':
        top = triggerRect.top + triggerRect.height / 2 - tooltipHeight / 2;
        left = triggerRect.right + padding;
        break;
      default:
        top = triggerRect.top - tooltipHeight - padding;
        left = triggerRect.left + triggerRect.width / 2 - tooltipWidth / 2;
    }

    // Flip position if it would overflow viewport
    if (position === 'top' && top < viewportPadding) {
      top = triggerRect.bottom + padding;
      finalPosition = 'bottom';
    } else if (position === 'bottom' && top + tooltipHeight > viewport.height - viewportPadding) {
      top = triggerRect.top - tooltipHeight - padding;
      finalPosition = 'top';
    } else if (position === 'left' && left < viewportPadding) {
      left = triggerRect.right + padding;
      finalPosition = 'right';
    } else if (position === 'right' && left + tooltipWidth > viewport.width - viewportPadding) {
      left = triggerRect.left - tooltipWidth - padding;
      finalPosition = 'left';
    }

    // Keep tooltip within horizontal bounds
    if (left < viewportPadding) {
      left = viewportPadding;
    } else if (left + tooltipWidth > viewport.width - viewportPadding) {
      left = viewport.width - tooltipWidth - viewportPadding;
    }

    // Keep tooltip within vertical bounds
    if (top < viewportPadding) {
      top = viewportPadding;
    } else if (top + tooltipHeight > viewport.height - viewportPadding) {
      top = viewport.height - tooltipHeight - viewportPadding;
    }

    // Calculate arrow position to point to trigger center
    const triggerCenterX = triggerRect.left + triggerRect.width / 2;
    const triggerCenterY = triggerRect.top + triggerRect.height / 2;

    let arrowLeft = 'auto';
    let arrowTop = 'auto';
    let arrowRight = 'auto';
    let arrowBottom = 'auto';

    if (finalPosition === 'top' || finalPosition === 'bottom') {
      const arrowX = triggerCenterX - left;
      arrowLeft = `${Math.max(12, Math.min(arrowX, tooltipWidth - 12))}px`;
    } else {
      const arrowY = triggerCenterY - top;
      arrowTop = `${Math.max(8, Math.min(arrowY, tooltipHeight - 8))}px`;
    }

    setActualPosition(finalPosition);
    setTooltipStyle({
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`,
      zIndex: 99999,
    });
    setArrowStyle({
      left: arrowLeft,
      top: arrowTop,
      right: arrowRight,
      bottom: arrowBottom,
    });
  }, [position]);

  const showTooltip = () => {
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delay);
  };

  const hideTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
  };

  useEffect(() => {
    if (isVisible) {
      calculatePosition();
      // Recalculate after a frame to get accurate tooltip dimensions
      requestAnimationFrame(calculatePosition);
    }
  }, [isVisible, calculatePosition]);

  useEffect(() => {
    if (isVisible) {
      window.addEventListener('scroll', calculatePosition, true);
      window.addEventListener('resize', calculatePosition);
      return () => {
        window.removeEventListener('scroll', calculatePosition, true);
        window.removeEventListener('resize', calculatePosition);
      };
    }
  }, [isVisible, calculatePosition]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const getArrowClasses = () => {
    const base = 'absolute w-2 h-2 bg-slate-800 rotate-45';
    switch (actualPosition) {
      case 'top':
        return `${base} -bottom-1`;
      case 'bottom':
        return `${base} -top-1`;
      case 'left':
        return `${base} -right-1`;
      case 'right':
        return `${base} -left-1`;
      default:
        return `${base} -bottom-1`;
    }
  };

  const getTransformOrigin = () => {
    switch (actualPosition) {
      case 'top': return 'bottom center';
      case 'bottom': return 'top center';
      case 'left': return 'right center';
      case 'right': return 'left center';
      default: return 'bottom center';
    }
  };

  if (!content) return children;

  return (
    <>
      <div
        ref={triggerRef}
        className={`inline-flex ${className}`}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
      >
        {children}
      </div>

      {createPortal(
        <AnimatePresence>
          {isVisible && (
            <motion.div
              ref={tooltipRef}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.12, ease: 'easeOut' }}
              style={{
                ...tooltipStyle,
                transformOrigin: getTransformOrigin(),
              }}
              className="pointer-events-none"
            >
              <div className="relative bg-slate-800 text-white text-xs font-medium px-3 py-2 rounded-lg shadow-xl max-w-xs">
                {content}
                <div
                  className={getArrowClasses()}
                  style={{
                    left: arrowStyle.left !== 'auto' ? arrowStyle.left : undefined,
                    top: arrowStyle.top !== 'auto' ? arrowStyle.top : undefined,
                    transform: `translateX(-50%) rotate(45deg)`,
                  }}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}

/**
 * Tooltip with rich content support
 */
export function RichTooltip({
  children,
  title,
  items = [],
  position = 'top',
  delay = 300,
  className = ''
}) {
  const content = (
    <div className="space-y-1.5 min-w-[140px]">
      {title && <div className="font-bold text-teal-300 text-sm">{title}</div>}
      {items.map((item, idx) => (
        <div key={idx} className="flex items-center gap-2">
          {item.icon && <span className="text-slate-400">{item.icon}</span>}
          <span className="text-slate-400">{item.label}:</span>
          <span className="font-semibold text-white">{item.value}</span>
        </div>
      ))}
    </div>
  );

  return (
    <Tooltip content={content} position={position} delay={delay} className={className}>
      {children}
    </Tooltip>
  );
}
